import { useEffect,useState } from "react";
import { Box,Button,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,Typography } from "@mui/material";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";

  if(contentType.includes("application/json")){
    return response.json();
  }

  if(!response.ok){
    throw new Error("El servidor no respondió correctamente.");
  }

  return {};
};

export default function RefereeCard({open,referee,onClose}){
  const {colors,typography,shape}=useAppTheme();
  const {showError}=useAlert();

  const [loading,setLoading]=useState(false);
  const [data,setData]=useState(null);
  const [openingPdf,setOpeningPdf]=useState(false);

  useEffect(()=>{
    if(!open||!referee?.qr_token){
      setData(null);
      return;
    }

    let active=true;

    const load=async()=>{
      try{
        setLoading(true);

        const response=await fetch(
          `/matches/referees/${referee.qr_token}/carnet/?output=data`,
          {
            credentials:"include"
          }
        );

        const result=await readResponse(response);

        if(!response.ok){
          throw new Error(
            result.error||
            result.detail||
            "No se pudo generar el carnet."
          );
        }

        if(active){
          setData(result);
        }
      }catch(error){
        if(active){
          setData(null);

          showError(
            error.message||
            "No se pudo generar el carnet."
          );
        }
      }finally{
        if(active){
          setLoading(false);
        }
      }
    };

    load();

    return ()=>{
      active=false;
    };
  },[open,referee?.qr_token]);

  const handlePdf=async()=>{
    if(!referee?.qr_token||openingPdf) return;

    try{
      setOpeningPdf(true);

      const response=await fetch(
        `/matches/referees/${referee.qr_token}/carnet/?output=pdf`,
        {
          credentials:"include"
        }
      );

      if(!response.ok){
        const result=await readResponse(response);

        throw new Error(
          result.error||
          result.detail||
          "No se pudo generar el PDF."
        );
      }

      const blob=await response.blob();
      const url=URL.createObjectURL(blob);
      const newWindow=window.open(url,"_blank");

      if(!newWindow){
        URL.revokeObjectURL(url);
        throw new Error("El navegador bloqueó la apertura del PDF.");
      }

      setTimeout(()=>{
        URL.revokeObjectURL(url);
      },60000);
    }catch(error){
      showError(
        error.message||
        "No se pudo generar el PDF."
      );
    }finally{
      setOpeningPdf(false);
    }
  };

  const carnet=data?.referee;
  const qr=data?.qr;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      disableScrollLock
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface
        }
      }}
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:700,
            color:colors.text
          }}
        >
          Carnet del árbitro
        </Typography>

        <Typography
          sx={{
            mt:0.3,
            fontSize:typography.sizes.sm,
            color:colors.textSecondary
          }}
        >
          Vista previa de la identificación del árbitro.
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading?(
          <Box
            sx={{
              height:300,
              display:"flex",
              alignItems:"center",
              justifyContent:"center"
            }}
          >
            <CircularProgress
              size={26}
              sx={{color:colors.secondary}}
            />
          </Box>
        ):carnet&&qr?(
          <Box
            sx={{
              display:"flex",
              justifyContent:"center",
              py:2,
              overflowX:"auto"
            }}
          >
            <Box
              sx={{
                width:600,
                height:379,
                minWidth:600,
                border:`1px solid ${colors.border}`,
                borderRadius:shape.radiusLarge,
                backgroundColor:colors.surface,
                overflow:"hidden",
                display:"flex",
                flexDirection:"column"
              }}
            >
              <Box
                sx={{
                  height:84,
                  px:2.5,
                  display:"flex",
                  alignItems:"center",
                  backgroundColor:colors.primary
                }}
              >
                <Box sx={{minWidth:0}}>
                  <Typography
                    sx={{
                      fontSize:typography.sizes.lg,
                      fontWeight:700,
                      color:colors.tableHeaderText,
                      lineHeight:1.15
                    }}
                  >
                    TORNEOS
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.xs,
                      color:colors.tableHeaderText,
                      opacity:0.72,
                      letterSpacing:"0.06em"
                    }}
                  >
                    CARNET DE ÁRBITRO
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  flex:1,
                  p:2,
                  display:"grid",
                  gridTemplateColumns:"150px 1fr 126px",
                  gap:2
                }}
              >
                <Box
                  sx={{
                    width:150,
                    height:238,
                    borderRadius:shape.radius,
                    backgroundColor:colors.backgroundSoft,
                    border:`1px solid ${colors.border}`,
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"center",
                    justifyContent:"center",
                    px:2
                  }}
                >
                  <Box
                    sx={{
                      width:72,
                      height:72,
                      borderRadius:"50%",
                      backgroundColor:colors.secondary,
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"center",
                      mb:2
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize:"1.7rem",
                        fontWeight:700,
                        color:colors.buttonPrimaryText
                      }}
                    >
                      {String(carnet.full_name||"A")
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize:typography.sizes.sm,
                      fontWeight:700,
                      color:colors.text,
                      textAlign:"center"
                    }}
                  >
                    ÁRBITRO
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.6,
                      fontSize:typography.sizes.xs,
                      color:colors.textMuted,
                      textAlign:"center"
                    }}
                  >
                    Identificación deportiva
                  </Typography>
                </Box>

                <Box
                  sx={{
                    minWidth:0,
                    pt:0.5
                  }}
                >
                  <Typography
                    sx={{
                      fontSize:typography.sizes.xs,
                      fontWeight:600,
                      color:colors.textMuted,
                      letterSpacing:"0.05em"
                    }}
                  >
                    ÁRBITRO
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.xl,
                      fontWeight:700,
                      color:colors.text,
                      lineHeight:1.2
                    }}
                  >
                    {carnet.full_name}
                  </Typography>

                  <Box
                    sx={{
                      my:2,
                      borderTop:`1px solid ${colors.border}`
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize:typography.sizes.xs,
                      fontWeight:600,
                      color:colors.textMuted,
                      letterSpacing:"0.05em"
                    }}
                  >
                    IDENTIFICACIÓN
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.lg,
                      fontWeight:700,
                      color:colors.text
                    }}
                  >
                    {carnet.identification}
                  </Typography>

                  <Box
                    sx={{
                      my:1.7,
                      borderTop:`1px solid ${colors.border}`
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize:typography.sizes.xs,
                      fontWeight:600,
                      color:colors.textMuted,
                      letterSpacing:"0.05em"
                    }}
                  >
                    TELÉFONO
                  </Typography>

                  <Typography
                    sx={{
                      mt:0.5,
                      fontSize:typography.sizes.md,
                      fontWeight:600,
                      color:colors.text
                    }}
                  >
                    {carnet.phone||"No registrado"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display:"flex",
                    flexDirection:"column",
                    alignItems:"center",
                    justifyContent:"center"
                  }}
                >
                  <Box
                    sx={{
                      mb:1.5,
                      px:1.5,
                      py:0.6,
                      borderRadius:shape.radiusSmall,
                      backgroundColor:colors.secondary
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize:"0.64rem",
                        fontWeight:600,
                        color:colors.buttonPrimaryText,
                        letterSpacing:"0.04em"
                      }}
                    >
                      QR ÁRBITRO
                    </Typography>
                  </Box>

                  <Box
                    component="img"
                    src={qr.image}
                    alt="QR del árbitro"
                    sx={{
                      width:116,
                      height:116,
                      objectFit:"contain"
                    }}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  height:12,
                  backgroundColor:colors.secondary
                }}
              />
            </Box>
          </Box>
        ):(
          <Box
            sx={{
              py:5,
              textAlign:"center"
            }}
          >
            <Typography
              sx={{
                fontSize:typography.sizes.md,
                color:colors.textSecondary
              }}
            >
              No se pudo mostrar el carnet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px:3,
          py:2,
          borderTop:`1px solid ${colors.border}`
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{
            color:colors.buttonText,
            textTransform:"none"
          }}
        >
          Cerrar
        </Button>

        <Button
          size="small"
          variant="contained"
          disabled={!carnet||!qr||openingPdf}
          onClick={handlePdf}
          sx={{
            px:2,
            textTransform:"none",
            backgroundColor:colors.secondary,
            "&:hover":{
              backgroundColor:colors.secondaryHover
            }
          }}
        >
          {openingPdf?"Generando...":"Ver PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}